/**
 * TerraSol Frontend Application Entry Point
 *
 * Think of this file like your main.py or main.go - it's where the application starts.
 * This file runs when a user loads any page in their browser.
 *
 * For Backend Developers:
 * - This is like Flask app.py or Django settings.py - it configures and starts the app
 * - Imports work like Python imports or Go imports
 * - The application runs in the user's browser, not on your server
 */

// STEP 1: Import third-party libraries (like pip install or go get)
// Note: Google Maps functionality is now in a separate bundle (google-maps-entry.js)
// and only loads on pages that need it, keeping this main bundle smaller
import Alpine from 'alpinejs' // Frontend framework (like Flask for backend)
import ajax from '@imacrayon/alpine-ajax' // AJAX plugin for making HTTP requests
import './style.css' // CSS styles (like static files in Django)

/**
 * STEP 2: Configure the Frontend Framework
 *
 * Alpine.js is our frontend framework - think of it like:
 * - Flask with blueprints and routes
 * - Express.js with middleware
 * - A lightweight way to make HTML pages interactive
 */

// Register Alpine Ajax plugin (like registering middleware in Flask)
// This allows us to make HTTP requests to our backend APIs
Alpine.plugin(ajax)

/**
 * STEP 3: Define the Main Application Component
 *
 * This is like defining a main class in Python or a struct in Go.
 * Alpine.data() creates a "component" that manages application state and behavior.
 *
 * Think of this like:
 * class App:
 *     def __init__(self):
 *         self.user = None
 *         self.messages = []
 *         self.loading = False
 */

// Main application component - this manages the entire app's state
Alpine.data('app', () => ({
  // Application state (like instance variables in a Python class)
  user: null, // Currently logged-in user (like session.user)
  messages: [], // UI messages to show user (like flash messages)
  loading: false, // Is app currently loading? (boolean flag)

  /**
   * Application Initialization Method
   *
   * This runs when the app starts - like __init__ in Python or main() in Go.
   * The 'async' keyword means this function can wait for things to complete
   * (like async/await in Python or goroutines in Go).
   */
  async init() {
    console.log('TerraSol app initializing...')

    // Load user state from server (like checking authentication)
    await this.loadUserState()

    // Load initial content based on current path (like routing)
    await this.loadInitialContent()

    // Set up navigation handlers (like URL routing)
    this.setupNavigation()

    // Set up global AJAX handlers (like middleware for HTTP requests)
    this.setupAjaxHandlers()

    console.log('TerraSol app initialized')
  },

  /**
   * Load User Authentication State
   *
   * This is like checking if a user is logged in by calling your backend API.
   * Similar to: user = requests.get('/api/auth/me').json() in Python
   */
  async loadUserState() {
    try {
      // fetch() is like requests.get() in Python or http.Get() in Go
      const response = await fetch('/api/auth/me')
      if (response.ok) {
        // Convert response to JSON (like response.json() in Python requests)
        this.user = await response.json()
      } else {
        this.user = null
      }
    } catch (error) {
      // Error handling (like try/except in Python or panic handling in Go)
      console.error('Failed to load user state:', error)
      this.user = null
    }
  },

  /**
   * Load Initial Page Content
   *
   * This is like URL routing in Flask or Django - it looks at the current URL
   * and loads the appropriate content from the backend.
   *
   * Think of this like:
   * @app.route('/projects/search')
   * def search_page():
   *     return render_template('search.html')
   */
  async loadInitialContent() {
    // Get current URL path (like request.path in Flask)
    const path = window.location.pathname

    // Map browser URLs to backend component endpoints (like URL routing)
    // This is like Django's urls.py or Flask's @app.route decorators
    const routeMap = {
      '/': '/api/components/projects/search',
      '/projects/search': '/api/components/projects/search',
      '/users/login': '/api/components/users/login',
      '/users/register': '/api/components/users/register',
      '/users/profile': '/api/components/users/profile',
      '/developer/projects': '/api/components/developer/projects',
    }

    // Get the backend endpoint for this URL (with fallback default)
    const componentUrl = routeMap[path] || '/api/components/projects/search'

    // Load content from backend (like making an API call)
    await this.loadContent(componentUrl, false)

    // Note: Page-specific initialization is now handled directly in templates
    // (like JavaScript includes in individual Django templates)
  },

  /**
   * Load Content from Backend API
   *
   * This is like making an AJAX request to load new page content without
   * refreshing the entire page. Similar to:
   *
   * Python: requests.get(url).text
   * Go: http.Get(url) then read response body
   *
   * But instead of returning data, it updates the page content directly.
   */
  async loadContent(url, updateHistory = true) {
    // Set loading state (like showing a spinner)
    this.loading = true

    try {
      // Make HTTP request to backend (like calling your API)
      const response = await fetch(url)

      if (response.ok) {
        // Get HTML content from response (like response.text in Python)
        const content = await response.text()

        // Update page content (like updating DOM - the HTML structure)
        // This is like injecting new HTML into the page
        document.getElementById('main-content').innerHTML = content

        // Update browser URL without page refresh (like SPA routing)
        if (updateHistory) {
          const displayPath = this.getDisplayPath(url)
          if (window.location.pathname !== displayPath) {
            // Update browser history (like changing URL in address bar)
            history.pushState({ componentUrl: url }, '', displayPath)
          }
        }

        // Clear any existing messages (like clearing flash messages)
        this.clearMessages()

        // Re-initialize Alpine.js components in the new content
        // This is needed because we just replaced the HTML content
        if (this.$nextTick) {
          // $nextTick waits for DOM updates (like waiting for render in React)
          this.$nextTick(() => {
            Alpine.initTree(document.getElementById('main-content'))
            // Note: Page-specific functionality is now handled directly in templates
          })
        } else {
          // Fallback timing approach
          setTimeout(() => {
            Alpine.initTree(document.getElementById('main-content'))
          }, 0)
        }
      } else {
        // Handle HTTP errors (like 404, 500, etc.)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      // Error handling (like try/except in Python)
      console.error('Failed to load content:', error)
      this.showError('Failed to load content. Please try again.')
    } finally {
      // Always runs, even if there's an error (like finally block)
      this.loading = false
    }
  },

  /**
   * Initialize Page-Specific Functionality
   *
   * This method can be used for page-specific JavaScript initialization.
   * Currently, we handle this directly in templates instead.
   *
   * Think of this like Django's per-view custom JavaScript or
   * Flask's template-specific code.
   */
  initPageSpecific(componentUrl) {
    // Page-specific initialization can be added here as needed
    // For now, modules are initialized directly in templates
    // (like including JavaScript directly in individual Django templates)
  },

  /**
   * Convert Backend API URLs to User-Friendly URLs
   *
   * This maps internal API endpoints to clean URLs that users see.
   * Like URL reversing in Django or named routes in Flask.
   *
   * Example:
   * '/api/components/projects/search' -> '/projects/search'
   */
  getDisplayPath(componentUrl) {
    // Reverse mapping from API endpoints to user-friendly URLs
    // This is like Django's reverse() function or Flask's url_for()
    const pathMap = {
      '/api/components/projects/search': '/projects/search',
      '/api/components/users/login': '/users/login',
      '/api/components/users/register': '/users/register',
      '/api/components/users/profile': '/users/profile',
      '/api/components/developer/projects': '/developer/projects',
    }

    return pathMap[componentUrl] || '/'
  },

  /**
   * Set Up Browser Navigation Handling
   *
   * This handles browser back/forward buttons for Single Page Application (SPA).
   * Like handling URL changes in a React Router or Vue Router.
   *
   * Without this, back/forward buttons wouldn't work in our app.
   */
  setupNavigation() {
    // Listen for browser back/forward button clicks
    // This is like handling route changes in a web framework
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.componentUrl) {
        // User clicked back/forward - load the previous page content
        this.loadContent(e.state.componentUrl, false)
      } else {
        // Fallback for initial page load or direct URL access
        this.loadInitialContent()
      }
    })
  },

  /**
   * Set Up Global AJAX Event Handlers
   *
   * This is like setting up middleware or global error handlers in backend frameworks.
   * It listens for AJAX events across the entire application.
   *
   * Similar to:
   * - Django middleware for handling requests/responses
   * - Flask error handlers
   * - Express.js middleware
   */
  setupAjaxHandlers() {
    // Handle AJAX loading states (like showing/hiding loading spinners)
    document.addEventListener('alpine:ajax:before', () => {
      this.loading = true // Show loading indicator
    })

    document.addEventListener('alpine:ajax:after', () => {
      this.loading = false // Hide loading indicator
    })

    // Handle AJAX errors globally (like global exception handling)
    document.addEventListener('alpine:ajax:error', (e) => {
      console.error('AJAX Error:', e.detail)
      this.showError('An error occurred. Please try again.')
      this.loading = false
    })

    // Handle successful form submissions that might update user state
    // This is like checking if certain API calls require state updates
    document.addEventListener('alpine:ajax:after', async (e) => {
      const response = e.detail.response

      // Check if this was a login/register request that might change user state
      // (like checking if we need to refresh session data)
      if (
        response.url.includes('/api/components/users/login') ||
        response.url.includes('/api/components/users/register')
      ) {
        await this.loadUserState() // Refresh user data
      }
    })
  },

  /**
   * User Logout Method
   *
   * This calls the backend logout API and updates the frontend state.
   * Like calling a logout endpoint and clearing session data.
   *
   * Similar to:
   * def logout_view(request):
   *     logout(request)
   *     return redirect('home')
   */
  async logout() {
    try {
      // Call backend logout API (like POST to /logout endpoint)
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'same-origin', // Include cookies for authentication
      })

      if (response.ok) {
        // Clear user state (like clearing session)
        this.user = null
        this.showSuccess('Logged out successfully')

        // Redirect to home page (like redirect in backend)
        await this.loadContent('/api/components/projects/search')
      } else {
        throw new Error('Logout failed')
      }
    } catch (error) {
      // Handle logout errors (like network issues)
      console.error('Logout error:', error)
      this.showError('Failed to logout. Please try again.')
    }
  },

  /**
   * UI Message Display Methods
   *
   * These are like flash messages in Django or Flask - they show temporary
   * messages to the user (success, error, warning, info).
   *
   * Like Django's messages.error() or Flask's flash() functions.
   */

  showError(message) {
    // Create error message object (like adding to messages framework)
    this.messages = [
      {
        type: 'error',
        text: message,
        id: Date.now(), // Unique ID (like timestamp for identification)
      },
    ]

    // Auto-dismiss after 5 seconds (like temporary flash messages)
    setTimeout(() => {
      this.clearMessages()
    }, 5000)
  },

  showSuccess(message) {
    // Success message (green notification)
    this.messages = [
      {
        type: 'success',
        text: message,
        id: Date.now(),
      },
    ]

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      this.clearMessages()
    }, 3000)
  },

  showWarning(message) {
    // Warning message (yellow notification)
    this.messages = [
      {
        type: 'warning',
        text: message,
        id: Date.now(),
      },
    ]

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      this.clearMessages()
    }, 4000)
  },

  showInfo(message) {
    // Info message (blue notification)
    this.messages = [
      {
        type: 'info',
        text: message,
        id: Date.now(),
      },
    ]

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      this.clearMessages()
    }, 4000)
  },

  clearMessages() {
    // Clear all messages (like clearing flash messages)
    this.messages = []
  },

  dismissMessage(messageId) {
    // Remove specific message by ID (like dismissing individual notifications)
    // This uses array filter (like list comprehension in Python)
    this.messages = this.messages.filter((msg) => msg.id !== messageId)
  },
})) // End of main Alpine.data('app') component definition

/**
 * STEP 5: Global Utility Functions
 *
 * These functions are exposed globally so templates can call them directly.
 * Like having global helper functions in Django or Flask that templates can use.
 *
 * Templates can call: window.loadComponent(url) or window.showMessage(type, msg)
 */

// Global function to load content from templates (like AJAX page loading)
window.loadComponent = (url) => {
  // Get the main app instance (like accessing global app state)
  const app = Alpine.$data(document.body)
  if (app && app.loadContent) {
    app.loadContent(url) // Call the app's loadContent method
  }
}

// Global function to show messages from templates (like flash messages)
window.showMessage = (type, message) => {
  // Get the main app instance
  const app = Alpine.$data(document.body)
  if (app) {
    // Call appropriate message method based on type (like switch statement)
    switch (type) {
      case 'error':
        app.showError(message)
        break
      case 'success':
        app.showSuccess(message)
        break
      case 'warning':
        app.showWarning(message)
        break
      case 'info':
        app.showInfo(message)
        break
    }
  }
}

/**
 * STEP 6: Form Handler Component
 *
 * This is a reusable Alpine.js component for handling form submissions.
 * Like a form mixin or base class in Django that handles common form logic.
 *
 * Usage in templates:
 * <form x-data="form()" @submit="submitForm($event)">
 */

// Form handler Alpine component (like a reusable form class)
Alpine.data('form', (options = {}) => ({
  // Form state (like form validation state in Django)
  submitting: false, // Is form currently being submitted?
  errors: {}, // Form validation errors (like form.errors in Django)

  /**
   * Handle Form Submission
   *
   * This handles AJAX form submission without page refresh.
   * Like handling POST requests in Django/Flask but on the frontend.
   */
  async submitForm(event, options = {}) {
    // Prevent default form submission (don't reload page)
    event.preventDefault()

    // Set submitting state (like showing loading spinner)
    this.submitting = true
    this.errors = {} // Clear previous errors

    // Get form element and create form data (like request.POST in Django)
    const form = event.target
    const formData = new FormData(form) // This is like form data in backend

    try {
      // Submit form via AJAX (like making POST request to backend)
      const response = await fetch(form.action || window.location.href, {
        method: form.method || 'POST',
        body: formData, // Send form data (like request body)
      })

      if (response.ok) {
        // Success - update page content with response
        const content = await response.text()
        document.getElementById('main-content').innerHTML = content

        // Re-initialize Alpine components in new content
        Alpine.initTree(document.getElementById('main-content'))

        // Show success message if provided
        if (options.successMessage) {
          window.showMessage('success', options.successMessage)
        }

        // Reload user state if this was an authentication form
        // (like updating session after login/register)
        if (form.action.includes('/login') || form.action.includes('/register')) {
          await Alpine.$data(document.body).loadUserState()
        }
      } else {
        // Handle form errors (like form validation errors)
        const errorContent = await response.text()
        document.getElementById('main-content').innerHTML = errorContent
        Alpine.initTree(document.getElementById('main-content'))
      }
    } catch (error) {
      // Handle network or other errors
      console.error('Form submission error:', error)
      window.showMessage('error', 'Form submission failed. Please try again.')
    } finally {
      // Always reset submitting state (like finally block)
      this.submitting = false
    }
  },
}))

/**
 * STEP 4: Start the Application
 *
 * This is like app.run() in Flask or starting your Django server.
 * It initializes Alpine.js and starts the frontend application.
 */

// Start Alpine.js framework (like starting the web server)
Alpine.start()

// Log that application is ready (like "Server running on port 8000")
console.log('TerraSol Alpine.js application loaded')

/**
 * APPLICATION SUMMARY FOR BACKEND DEVELOPERS:
 *
 * This file sets up a Single Page Application (SPA) that:
 * 1. Loads content from your backend APIs without page refreshes
 * 2. Handles user authentication state
 * 3. Manages navigation and browser history
 * 4. Provides reusable form handling
 * 5. Shows user messages (like flash messages)
 * 6. Integrates with custom modules (like Google Maps)
 *
 * Think of it as the frontend equivalent of your backend application:
 * - Alpine.js = Your web framework (Flask/Django)
 * - Components = Your views/controllers
 * - fetch() calls =

   **/
